import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Address, AutoCompleteAddressResponse } from '../domain/Address';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { ClientService } from './client.service';

@Injectable({
  providedIn: 'root',
})
export class AddressService {
  validationResult = new BehaviorSubject<ValidateAddressResponse>({} as ValidateAddressResponse)
  dawaResultFound$ = new BehaviorSubject<any[]>(new Array<any>());
  dawaResultFound = new Array<any>();
  kdsToken = "adressevaelger123";
  constructor(private httpClient: HttpClient, private clientService: ClientService) { }

  autoCompleteAddress(searchInput: string) {
    // return this.httpClient.post<AutoCompleteAddressResponse>(`${environment.apiUrl}/e/address/autocomplete`, { searchInput });
    if (this.clientService.countries.filter(x => x !== "DK").length > 0)
      return this.httpClient.post<UpdatedAutoCompleteAddressResponse>(`${environment.apiUrl}/e/address/autocomplete`, { searchInput });
    else
      return of({ items: [] } as UpdatedAutoCompleteAddressResponse);
  }

  ValidateAddress(adresseId) {

    this.httpClient.post<ValidateAddressResponse>(`${environment.apiUrl}/e/address/validate`, { id: adresseId }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.validationResult.error({});
        return throwError(() => error);
      })
    ).subscribe(x => this.validationResult.next(x));
  }

  updateShipmentAddress(address: any, shipmentId: any) {
    if (address.addressId) {
      address.id = address.addressId;
    }
    var updateAddressCommand = {
      address: address,
      shipmentId: shipmentId,
    };
    return this.httpClient.post<IUpdateAddressResponse>(`${environment.apiUrl}/shipments/address/update`, updateAddressCommand);
  }

  getAddress(addressId: string) {
    return this.httpClient.get<Address>(`${environment.apiUrl}/e/address?id=${addressId}`);
  }

  KdsAutocomplete(searchString: string): Observable<KdsAddressSuggestion[]> {
    let search = searchString.trim();
    if (!search)
      return of([]);

    let url = 'https://adressevaelger.dk/husnumre/soeg';
    let params = { tekst: search.slice(0, 73), token: this.kdsToken, maksimum: '100' };
    // this.httpClient.get<KdsSearchResponse>(url, { params }).subscribe(x => console.log("pure kds response: ", x));
    return this.httpClient.get<KdsSearchResponse>(url, { params })
      .pipe(map(response => (response.fund ?? [])
        //.filter(x => x.type === 'husnummer' && !!x.id)
        .map(x => ({
          street: `${x.vejnavn ?? ''} ${x.husnummer ?? ''}`.trim() || x.titel,
          zipCode: x.postnr ?? x.titel.split(',')[x.titel.split(',').length - 1].match(/\d+/) + "",
          city: x.postdistrikt ?? x.titel.split(',')[x.titel.split(',').length - 1].match(/\p{L}+(?:[ -]\p{L}+)*/u)?.[0],
          countryCode: 'DK',
          latitude: null,
          longitude: null,
          validatedBy: 'KDS',
          kdsId: x.id!,
          isForslag: x.type == 'vejnavn'

        }))
      ),
        catchError(error => {
          console.error('KDS autocomplete failed', error);
          return of([]);
        })
      );
  }
  // this gets the lat, long from kds
  resolveKdsAddress(id: string): Observable<AutoComplteItems> {
    let url = `https://adressevaelger.dk/husnumre/${id}`;
    let params = { token: this.kdsToken };

    return this.httpClient.get<KdsHouseNumberResponse>(url, { params }).pipe(
      map(response => {
        const house = response.husnummer;
        if (!house) {
          throw new Error('KDS did not return a house number');
        }

        const point = house.adgangspunkt?.koordinater;

        if (!point) {
          throw new Error('KDS did not return coordinates');
        }

        const coordinates = this.epsg25832ToLatLong(point.x, point.y);

        return {
          street: `${house.vejnavn} ${house.husnummertekst}`.trim(),
          zipCode: house.postnummer?.postnr ?? '',
          city: house.postnummer?.navn ?? '',
          countryCode: 'DK',
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          validatedBy: 'KDS'
        };
      })
    );
  }

  // Converts to wgsm lat,long as we kown and love.
  epsg25832ToLatLong(easting: number, northing: number): { latitude: number; longitude: number } {
    const a = 6378137.0;
    const f = 1 / 298.257222101;
    const k0 = 0.9996;
    const zone = 32;

    const e2 = f * (2 - f);
    const ePrime2 = e2 / (1 - e2);

    const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
    const x = easting - 500000.0;
    const y = northing;

    const m = y / k0;

    const mu = m / (a * (1 - e2 / 4 -
      3 * Math.pow(e2, 2) / 64 -
      5 * Math.pow(e2, 3) / 256)
    );

    const phi1 = mu + (3 * e1 / 2 - 27 * Math.pow(e1, 3) / 32) *
      Math.sin(2 * mu) + (21 * Math.pow(e1, 2) / 16 - 55 * Math.pow(e1, 4) / 32) *
      Math.sin(4 * mu) + (151 * Math.pow(e1, 3) / 96) *
      Math.sin(6 * mu) + (1097 * Math.pow(e1, 4) / 512) *
      Math.sin(8 * mu);

    const sinPhi1 = Math.sin(phi1);
    const cosPhi1 = Math.cos(phi1);
    const tanPhi1 = Math.tan(phi1);

    const n1 = a / Math.sqrt(1 - e2 * Math.pow(sinPhi1, 2));
    const r1 = a * (1 - e2) / Math.pow(1 - e2 * Math.pow(sinPhi1, 2), 1.5);
    const t1 = Math.pow(tanPhi1, 2);
    const c1 = ePrime2 * Math.pow(cosPhi1, 2);

    const d = x / (n1 * k0);
    const latitudeRadians =
      phi1 - (n1 * tanPhi1 / r1) * (
        Math.pow(d, 2) / 2 -

        (5 + 3 * t1 + 10 * c1 - 4 * Math.pow(c1, 2) - 9 * ePrime2) *
        Math.pow(d, 4) / 24 + (61 + 90 * t1 + 298 * c1 + 45 * Math.pow(t1, 2) - 252 * ePrime2 - 3 * Math.pow(c1, 2)) *
        Math.pow(d, 6) / 720
      );

    const longitudeOrigin = ((zone - 1) * 6 - 180 + 3) * Math.PI / 180;

    const longitudeRadians =
      longitudeOrigin + (d - (1 + 2 * t1 + c1) * Math.pow(d, 3) / 6 + (5 - 2 * c1 + 28 * t1 - 3 * Math.pow(c1, 2) + 8 * ePrime2 + 24 * Math.pow(t1, 2)) * Math.pow(d, 5) / 120) / cosPhi1;

    return { latitude: latitudeRadians * 180 / Math.PI, longitude: longitudeRadians * 180 / Math.PI };
  }
}

export interface IUpdateAddressResponse {
  id: string;
}

export interface UpdatedAutoCompleteAddressResponse {
  errors: any[];
  hasNext: boolean;
  hasPrevious: boolean
  isValid: boolean;
  items: Address[];
  page: number;
  perPage: number;
  size: number;
  totalPages: number;
}

export interface ValidateAddressErrorResponse {
  errors: any[]
}

export interface ValidateAddressResponse {
  actions: any
  errors: any[]
  isValid: true
  item: ValidateItemResponse;
}
interface ValidateItemResponse {
  id: string
  latitude: number;
  longitude: number;
  validated: boolean;
  validatedBy: string

}
export interface AutoCompleteAddreseResponseV2 {
  addreses: AutoComplteItems[];
}

export interface AutoComplteItems {
  street: string,
  city: string,
  countryCode: string,
  zipCode: string,
  longitude: number,
  latitude: number,
  validatedBy: string;
}

export interface KdsSearchResponse {
  status: string;
  beskrivelse: string;
  fund: KdsSearchResult[];
}

export interface KdsSearchResult {
  id?: string;
  type: 'vejnavn' | 'navngivenvejpostnummer' | 'husnummer';
  titel: string;
  vejnavn?: string;
  husnummer?: string;
  postnr?: string;
  postdistrikt?: string;

}

export interface KdsAddressSuggestion {
  street: string;
  city: string;
  countryCode: string;
  zipCode: string;
  latitude: null;
  longitude: null;
  validatedBy: string;
  // Used when selected.
  kdsId: string;
}

export interface KdsHouseNumberResponse {
  status: string;

  husnummer: {
    id_lokalid: string;
    husnummertekst: string;
    adgangsadressebetegnelse: string;
    vejnavn: string;

    adgangspunkt: {
      koordinater: {
        x: number;
        y: number;
      };
    };

    postnummer: {
      navn: string;
      postnr: string;
    };
  };
}